import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Steps, Row, Col, Tag, Spin } from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  FormOutlined,
  ApartmentOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { PHARMACY_SOPS_DATA, isSopHiddenByConfig } from '../data/pharmacyToolData.js';
import { pharmacyToolApi } from '../../../api/pharmacy-tool.api.js';
import { PharmacyDiagnosticForm } from '../components/PharmacyDiagnosticForm.jsx';
import { PharmacyFlowMap } from '../components/PharmacyFlowMap.jsx';
import { useToolAccess } from '../hooks/useToolAccess.js';
import '../styles/pharmacy-tool.css';

const LOCAL_STORAGE_KEY_CONFIG = 'thethao_pharmacy_tool_config';
const LOCAL_STORAGE_KEY_COMPLETED = 'thethao_pharmacy_tool_completed_sops';

export function PharmacyToolPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Master SOPs loaded from Database (fallback to local data if offline)
  const [sops, setSops] = useState(PHARMACY_SOPS_DATA);
  // Chưa mua công cụ SOP: chỉ tải được 3 quy trình dùng thử, còn lại hiện nút mua
  const { access: toolAccess } = useToolAccess();
  const sopAccess = toolAccess?.['quan-tri-thuc-chien'] || null;
  const [isLoadingSops, setIsLoadingSops] = useState(false);

  // Load configuration from localStorage or empty default
  const [config, setConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY_CONFIG);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.pharmacyName === 'Nhà Thuốc Thế Thảo') {
          parsed.pharmacyName = '';
        }
        return parsed;
      }
    } catch (e) {
      console.error('Error reading tool config from localStorage:', e);
    }
    return {
      pharmacyName: '',
      businessType: '',
      staffCount: '',
      ownerPresence: '',
      shiftCount: '',
      hasSoftware: '',
    };
  });

  // Load completed/applied SOPs from localStorage (default is empty)
  const [completedSops, setCompletedSops] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY_COMPLETED);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length === 1 && parsed[0] === 'QT-01') {
          return [];
        }
        return parsed;
      }
    } catch (e) {
      console.error('Error reading completed SOPs:', e);
    }
    return [];
  });

  // Check whether diagnostic answers have been completed
  const isDiagnosed = Boolean(
    config.pharmacyType &&
    config.staffCount &&
    config.ownerPresence &&
    config.shiftCount &&
    config.hasSoftware
  );

  // Current workflow step: 'form' (Bước 1: Chẩn đoán) vs 'results' (Bước 2: Quy trình matching)
  const stepParam = searchParams.get('step');
  const [currentStep, setCurrentStep] = useState(() => {
    if (stepParam === 'form') return 'form';
    if (stepParam === 'results' && isDiagnosed) return 'results';
    return isDiagnosed ? 'results' : 'form';
  });

  // Sync step with URL params and diagnosis state
  useEffect(() => {
    const step = searchParams.get('step');
    if (step && (step === 'form' || step === 'results') && step !== currentStep) {
      if (step === 'results' && !isDiagnosed) {
        setCurrentStep('form');
      } else {
        setCurrentStep(step);
      }
    }
  }, [searchParams, currentStep, isDiagnosed]);

  // Load SOPs from Database on mount
  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      setIsLoadingSops(true);
      try {
        const sopsFromDb = await pharmacyToolApi.getPharmacySops();
        if (isMounted && Array.isArray(sopsFromDb) && sopsFromDb.length > 0) {
          setSops(sopsFromDb);
        }
      } catch (err) {
        console.warn('Could not load SOPs from backend, using local fallback:', err);
      } finally {
        if (isMounted) setIsLoadingSops(false);
      }

      // Load remote config & progress if available
      try {
        const remoteConfig = await pharmacyToolApi.getPharmacyConfig();
        if (isMounted && remoteConfig && remoteConfig.pharmacyName) {
          setConfig((prev) => ({ ...prev, ...remoteConfig }));
        }
      } catch (e) { }

      try {
        const remoteProgress = await pharmacyToolApi.getPharmacyProgress();
        if (isMounted && remoteProgress && Array.isArray(remoteProgress.completedCodes)) {
          if (remoteProgress.completedCodes.length > 0) {
            setCompletedSops(remoteProgress.completedCodes);
          }
        }
      } catch (e) { }
    }

    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Handle updating config from form (Lưu nháp vào localStorage để phục vụ chẩn đoán)
  const handleUpdateConfig = (newConfig) => {
    setConfig(newConfig);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_CONFIG, JSON.stringify(newConfig));
    } catch (e) {
      console.error('Error auto-saving tool config to localStorage:', e);
    }
  };


  // Toggle SOP completion
  const handleToggleCompleteSop = async (code) => {
    const isNowCompleted = !completedSops.includes(code);
    const newCompleted = isNowCompleted
      ? [...completedSops, code]
      : completedSops.filter((c) => c !== code);

    setCompletedSops(newCompleted);

    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_COMPLETED, JSON.stringify(newCompleted));
    } catch (e) {
      console.error('Error saving completed SOPs to localStorage:', e);
    }

    // Synchronize progress to backend database
    try {
      await pharmacyToolApi.toggleSopProgress(code, isNowCompleted);
    } catch (e) {
      console.warn('Backend toggle note:', e);
    }
  };

  // Switch step
  const handleGoToStep = (targetStep) => {
    if (targetStep === 'results' && !isDiagnosed) {
      toast.warning('Vui lòng hoàn thành các câu hỏi chẩn đoán và bấm Hoàn tất để hệ thống xuất bản quy trình!');
      return;
    }
    setCurrentStep(targetStep);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('step', targetStep);
    newParams.delete('tab');
    setSearchParams(newParams);
    window.scrollTo({ top: 220, behavior: 'smooth' });
  };

  // When user submits the diagnostic form:
  const handleSubmitDiagnostic = async (submittedConfig) => {
    const activeConfig = submittedConfig || config;
    setConfig(activeConfig);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_CONFIG, JSON.stringify(activeConfig));
      await pharmacyToolApi.savePharmacyConfig(activeConfig);
    } catch (e) {
      console.warn('Backend sync note:', e);
    }
    setCurrentStep('results');
    const newParams = new URLSearchParams(searchParams);
    newParams.set('step', 'results');
    newParams.delete('tab');
    setSearchParams(newParams);
    toast.success('Chẩn đoán hoàn tất! Đã cập nhật và xuất bản bộ quy trình phù hợp với cơ sở của bạn.');
    window.scrollTo({ top: 220, behavior: 'smooth' });
  };

  // Calculate active SOPs count strictly matching the 6 diagnostic questions
  const activeSops = sops.filter((sop) => {
    return !isSopHiddenByConfig(sop.code, config);
  });

  return (
    <div className="pharmacy-tool-wrapper">
      {/* Hero Banner Header */}
      <div className="tool-hero-banner">
        <div className="main-container">
          <div style={{ marginBottom: 14 }}>
            <Link
              to="/tools"
              style={{
                color: '#d1fae5',
                fontSize: 13,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                textDecoration: 'none',
                background: 'rgba(255, 255, 255, 0.12)',
                padding: '4px 14px',
                borderRadius: 20,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowLeftOutlined /> Trung Tâm Công Cụ
            </Link>
          </div>

          <Row gutter={[24, 24]} align="middle" justify="space-between">
            <Col xs={24} md={14}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Tag color="#10b981" style={{ fontWeight: 700, borderRadius: 4, padding: '2px 8px' }}>
                  Xây dựng quy trình quản lý thực chiến (S.O.P) nhà thuốc
                </Tag>
                <span style={{ fontSize: 13, color: '#a7f3d0' }}>Dành riêng cho Quầy thuốc & Nhà thuốc</span>
              </div>
              <h1 className="tool-hero-title">
                Xây dựng hệ thống quy trình vận hành (S.O.P) cho nhà thuốc
              </h1>
              <p className="tool-hero-subtitle">
                Cá nhân hoá theo quy mô nhân sự và cung cấp trọn bộ 26 file Word (.docx) chuẩn sẵn sàng tải về chỉnh sửa & in ấn tại quầy.
              </p>
            </Col>

            {/* Hình ảnh quầy thuốc chuẩn GPP đang hoạt động */}
            <Col xs={24} md={10}>
              <div
                style={{
                  position: 'relative',
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  aspectRatio: '16/9',
                  background: 'rgba(0, 0, 0, 0.1)',
                }}
              >
                <img
                  src="/images/quay-thuoc-chuan-gpp.jpg"
                  alt="Quầy thuốc chuẩn GPP đang hoạt động"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '8px 14px',
                    background: 'linear-gradient(to top, rgba(0, 30, 20, 0.88) 0%, rgba(0, 30, 20, 0) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >


                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Main Content Area: Sequential Diagnostic Flow */}
      <div className="main-container" style={{ marginTop: 24 }}>
        <Spin spinning={isLoadingSops} tip="Đang tải dữ liệu quy trình từ cơ sở dữ liệu...">
          {/* Stepper Navigation Bar */}
          <div
            style={{
              marginBottom: 24,
              background: '#ffffff',
              padding: '16px 24px',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            }}
          >
            <Steps
              current={currentStep === 'form' ? 0 : 1}
              onChange={(targetIndex) => {
                if (targetIndex === 0) {
                  handleGoToStep('form');
                } else {
                  handleGoToStep('results');
                }
              }}
              items={[
                {
                  title: <span style={{ fontWeight: 700, fontSize: 14 }}>Bước 1: Nhận diện đặc thù quầy thuốc của bạn</span>,
                  description: isDiagnosed ? 'Đã hoàn thành 6/6 câu hỏi chẩn đoán' : 'Điền 6 câu hỏi để hệ thống nhận diện đặc thù',
                  icon: <FormOutlined />,
                },
                {
                  title: <span style={{ fontWeight: 700, fontSize: 14 }}>Bước 2: Bộ Quy Trình Vận Hành Thực Chiến</span>,
                  description: isDiagnosed
                    ? `Đã kích hoạt ${activeSops.length} quy trình phù hợp với cơ sở`
                    : 'Tự động xuất bản sau khi hoàn tất Bước 1',
                  icon: <ApartmentOutlined />,
                },
              ]}
            />
          </div>

          {/* Step 1: Chẩn đoán cấu hình quầy (6 câu hỏi) */}
          {currentStep === 'form' && (
            <PharmacyDiagnosticForm
              config={config}
              onChangeConfig={handleUpdateConfig}
              onSubmitDiagnostic={handleSubmitDiagnostic}
              activeSopCount={activeSops.length}
              totalSopCount={sops.length}
            />
          )}

          {/* Step 2: Bộ quy trình đã được chẩn đoán và matching 100% */}
          {currentStep === 'results' && (
            <PharmacyFlowMap
              key={`flow-map-${config.staffCount}-${config.ownerPresence}-${config.shiftCount}-${config.hasSoftware}-${config.pharmacyType}`}
              sops={sops}
              config={config}
              onNavigateToForm={() => handleGoToStep('form')}
              completedSopCodes={completedSops}
              onToggleCompleteSop={handleToggleCompleteSop}
              sopAccess={sopAccess}
            />
          )}
        </Spin>
      </div>
    </div>
  );
}
